import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, severityMeta } from "@/components/shared";

const SEVERITIES = [
  { id: "info", label: "Information" },
  { id: "safety", label: "Safety Notice" },
  { id: "meeting", label: "Meeting Point" },
  { id: "urgent", label: "Urgent Alert" },
];

export default function Communications() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("info");
  const [ups, setUps] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/band/updates").then(({ data }) => setUps(data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!title || !body) { toast.error("Add a title and message"); return; }
    setSending(true);
    try {
      await api.post("/band/updates", { title, body, severity });
      toast.success("Update sent to all participants");
      setTitle(""); setBody(""); setSeverity("info");
      load();
    } catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };

  return (
    <Page>
      <h1 className="font-head font-extrabold text-3xl">Communications</h1>
      <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Send updates and safety alerts to your band</p>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="cs-card p-5">
          <h2 className="font-head font-bold text-lg mb-4">New Message</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {SEVERITIES.map((s) => {
              const active = severity === s.id;
              const m = severityMeta(s.id);
              return (
                <button key={s.id} data-testid={`comms-severity-${s.id}`} onClick={() => setSeverity(s.id)}
                  className="cs-tap text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  style={{ background: active ? `${m.color}22` : "var(--cs-card-hi)", color: active ? m.color : "var(--cs-fg2)", border: `1px solid ${active ? m.color : "var(--cs-border)"}` }}>
                  <span className="rounded-full" style={{ width: 7, height: 7, background: m.color }} /> {s.label}
                </button>
              );
            })}
          </div>
          <input data-testid="comms-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Hydration break at 2 PM)"
            className="w-full rounded-xl px-4 outline-none mb-3" style={{ height: 48, background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "#fff", fontSize: 15 }} />
          <textarea data-testid="comms-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Message…"
            className="w-full rounded-xl p-4 outline-none" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "#fff", resize: "none", fontSize: 15 }} />
          <button data-testid="comms-send" onClick={send} disabled={sending}
            className="cs-tap w-full mt-4 magenta-grad rounded-xl text-white font-bold flex items-center justify-center gap-2" style={{ height: 50 }}>
            <Send size={18} /> {sending ? "Sending…" : "Send to band"}
          </button>
          <p className="text-xs mt-2" style={{ color: "#52525b" }}>Use Urgent sparingly — not every message is an emergency.</p>
        </div>

        <div className="cs-card p-5">
          <h2 className="font-head font-bold text-lg mb-4">Sent Feed</h2>
          <div className="space-y-3 max-h-[420px] overflow-y-auto no-scrollbar">
            {ups.map((u) => {
              const m = severityMeta(u.severity);
              return (
                <div key={u.id} data-testid={`comms-feed-${u.id}`} className="rounded-xl p-3" style={{ background: "var(--cs-card-hi)", borderLeft: `4px solid ${m.color}` }}>
                  <p className="text-[10px] font-bold uppercase" style={{ color: m.color }}>{m.label}</p>
                  <p className="font-semibold text-sm mt-0.5">{u.title}</p>
                  <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{u.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Page>
  );
}
